import { createORPCClient, ORPCError, onError } from '@orpc/client';
import {
  ContractRouterClient,
  inferRPCMethodFromContractRouter,
  ValidationError,
} from '@orpc/contract';
import { ResponseValidationPlugin } from '@orpc/contract/plugins';
import { contractRouter } from '@erp/rpc';
import {
  API_BUILD_TIME_HEADER,
  UI_BUILD_HASH_HEADER,
  UI_BUILD_TIME_HEADER,
} from '@erp/shared-utils';

import { RPCLink } from '@orpc/client/fetch';
import config from 'src/config';
import { notify403 } from 'src/api/notify403';
import {
  ImitationViewOnlyError,
  isImitationActive,
  isImitationViewOnlyError,
} from 'src/api/imitationState';
import { notifyViewOnly } from 'src/api/notifyViewOnly';
import { showToastOutsideReact } from 'src/services/toastService';
import { showUpdateOverlayOutsideReact } from 'src/services/updateOverlayService';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';
import { createRpcBatchLinkPlugin } from './batching';
import { isKeyedListCatalogCall, KEYED_LIST_PATHS } from './methodOverrides';
import { buildRpcUrl } from './rpcUrl';

function isOutputValidationError(error: unknown): boolean {
  return error instanceof ValidationError;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const AGGRESSIVE_NUDGE_THRESHOLD_MS = 3 * DAY_MS;

let versionMismatchToastShown = false;
let updateOverlayShown = false;

function showUpdateAvailableToast(): void {
  if (versionMismatchToastShown || updateOverlayShown) {
    return;
  }

  const shown = showToastOutsideReact({
    title: 'Update Available',
    message: 'A new version is available. Please refresh the page to continue.',
    color: 'warning',
    icon: 'sync',
    persistent: true,
    actionLabel: 'Refresh',
    onAction: () => window.location.reload(),
  });

  if (shown) {
    versionMismatchToastShown = true;
  }
}

function showUpdateRequiredOverlay(): void {
  if (updateOverlayShown) {
    return;
  }
  const shown = showUpdateOverlayOutsideReact();
  if (shown) {
    updateOverlayShown = true;
  }
}

const inferredRpcMethod = inferRPCMethodFromContractRouter(contractRouter);

/** Ends imitation — the one write that must survive the block below. */
const STOP_IMITATION_PATH_SUFFIX = '/admin/imitation/stop';
/** `BatchLinkPlugin` posts/gets envelopes at `<rpc base>/__batch__`. */
const BATCH_PATH_SUFFIX = '/__batch__';

/**
 * Whether an outgoing RPC request is allowed while a Gently admin is imitating.
 *
 * Belt-and-suspenders over the server-side guard, which is the actual security
 * boundary and rejects per procedure (per entry, inside batches). So this stays
 * deliberately permissive where it can't be sure:
 *
 * - Reads (GET/HEAD) always pass.
 * - Batch envelopes pass. Today they only ever carry GETs (`isBatchableRpcMethod`
 *   groups on `GET`), but the envelope's own method is an implementation detail
 *   of the plugin — blocking on it would break every read the moment batching
 *   changed shape, while letting one through costs nothing: the server guards
 *   each entry and the 403 comes back through the interceptor as a toast.
 * - Keyed catalog reads pass: they are GET procedures sent as POST (see
 *   methodOverrides.ts), and the server guard reads the contract's method.
 * - Everything else non-GET is a write, and is refused here so the user gets an
 *   explanation instead of a round trip that fails.
 */
function isRequestAllowedWhileImitating(request: Request): boolean {
  if (request.method === 'GET' || request.method === 'HEAD') {
    return true;
  }
  const { pathname } = new URL(request.url);
  return (
    pathname.endsWith(STOP_IMITATION_PATH_SUFFIX) ||
    pathname.endsWith(BATCH_PATH_SUFFIX) ||
    [...KEYED_LIST_PATHS].some((path) => pathname.endsWith(`/${path}`))
  );
}

/**
 * Create oRPC client with authentication integration
 */
const rpcLink = new RPCLink({
  url: buildRpcUrl(config.apiBaseUrl),

  // Use the same fetch configuration as existing API calls
  // This ensures cookies are included for authentication
  fetch: async (request: Request, init?: RequestInit) => {
    if (isImitationActive() && !isRequestAllowedWhileImitating(request)) {
      notifyViewOnly();
      throw new ImitationViewOnlyError();
    }

    let headers = new Headers(init?.headers ?? request.headers);
    if (config.uiBuildHash) {
      headers.set(UI_BUILD_HASH_HEADER, config.uiBuildHash);
    }
    if (config.uiBuildTime) {
      headers.set(UI_BUILD_TIME_HEADER, config.uiBuildTime);
    }
    headers = buildPosthogHeaders(headers);

    const response = await fetch(request, {
      ...init,
      headers,
      credentials: 'include', // Include cookies for session auth
    });

    if (!updateOverlayShown) {
      const backendBuildTime = response.headers.get(API_BUILD_TIME_HEADER);
      const uiBuildTime = config.uiBuildTime;
      if (backendBuildTime && uiBuildTime) {
        const backendTimeMs = Date.parse(backendBuildTime);
        const uiTimeMs = Date.parse(uiBuildTime);
        if (!Number.isNaN(backendTimeMs) && !Number.isNaN(uiTimeMs)) {
          const diffMs = Math.abs(backendTimeMs - uiTimeMs);
          if (diffMs >= AGGRESSIVE_NUDGE_THRESHOLD_MS) {
            showUpdateRequiredOverlay();
          } else if (diffMs >= DAY_MS && !versionMismatchToastShown) {
            showUpdateAvailableToast();
          }
        }
      }
    }

    return response;
  },
  // The contract's inferred methods, with one override: a catalog list call
  // carrying `keys` (up to 10k product keys) always travels as POST — see
  // methodOverrides.ts.
  method: (options, path, input) =>
    isKeyedListCatalogCall(path, input)
      ? 'POST'
      : inferredRpcMethod(options, path),

  plugins: [
    createRpcBatchLinkPlugin(),
    new ResponseValidationPlugin(contractRouter),
  ],

  interceptors: [
    onError((error) => {
      if (error instanceof ORPCError && error.code === 'FORBIDDEN') {
        // A write refused by the imitation guard is not a permissions problem —
        // the imitated user may well have the permission. Say what it actually
        // is instead of "Access Denied".
        if (isImitationViewOnlyError(error)) {
          notifyViewOnly();
        } else {
          notify403(error.message);
        }
      }
      if (isOutputValidationError(error)) {
        showUpdateRequiredOverlay();
      }
      throw error;
    }),
  ],
});

/**
 * oRPC client instance
 * Provides access to all backend RPC procedures
 */
const client: ContractRouterClient<typeof contractRouter> =
  createORPCClient(rpcLink);
export const orpc = createTanstackQueryUtils(client);

/**
 * Direct RPC client for imperative calls
 */
export const rpcClient = client;

type oRPCType = typeof orpc;

/**
 * Hook to return a function that invalidates RPC queries. This is useful for invalidating queries
 * after a mutation or query. This hook is mostly for devex and to prevent code duplication.
 *
 * @example
 * const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [orpc.memoryBank.list.queryOptions().queryKey]);
 *
 * const { mutateAsync: createNote } = useCreateMemoryBankNote();
 *
 * const handleCreateNote = async (noteContent: string) => {
 *   await createNote(noteContent);
 *   invalidateRpcQueryFn();
 * };
 *
 * @param fn - The function to call to get the query keys to invalidate.
 * @returns A function that invalidates the RPC queries.
 */
export function useInvalidateRpcQueryFn<T extends Record<string, unknown>>(
  fn: (orpc: oRPCType, input: T) => QueryKey[],
  dependencies: React.DependencyList = [],
) {
  const queryClient = useQueryClient();
  const memoizedFn = useCallback(fn, [fn, ...dependencies]);

  return useCallback(
    (input: T = {} as T) => {
      memoizedFn(orpc, input).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [memoizedFn, queryClient],
  );
}

/**
 * Router type for the RPC client
 * This will be properly typed once the backend router is available
 */
export type RpcRouter = typeof client;
